import React, { useState, useEffect } from 'react';
import { History, RotateCcw, AlertCircle, X, CheckCircle, Clock } from 'lucide-react';

export default function CMSVersionHistory({
  pageId,
  pageName = 'Page',
  client,
  onRollback,
  onClose,
}) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rollingBackId, setRollingBackId] = useState(null);
  const [confirmVersion, setConfirmVersion] = useState(null);

  useEffect(() => {
    if (!pageId || !client) return;
    let isMounted = true;
    setLoading(true);
    setError(null);

    client.get(`/cms/pages/${pageId}/versions`)
      .then((res) => {
        if (isMounted) {
          setVersions(res.data?.items || []);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (isMounted) {
          setError(err.response?.data?.detail || 'Failed to load version history');
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [pageId, client]);

  const handleExecuteRollback = async (version) => {
    setRollingBackId(version.id);
    try {
      const res = await client.post(`/cms/pages/${pageId}/rollback/${version.id}`);
      setConfirmVersion(null);
      if (onRollback) {
        onRollback(res.data);
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to rollback version');
    } finally {
      setRollingBackId(null);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'Unknown date';
    try {
      const d = new Date(isoString);
      return d.toLocaleString('en-IN', {
        dateStyle: 'medium',
        timeStyle: 'short',
      });
    } catch {
      return isoString;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs p-4 animate-in fade-in">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-xl max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-[#2B3380]/10 text-[#2B3380] rounded-xl">
              <History className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-800">Version History</h3>
              <p className="text-xs text-slate-500 font-medium">{pageName} • Published Snapshots</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-200/60 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-3">
          {loading && (
            <div className="py-12 flex flex-col items-center justify-center text-slate-400 gap-2">
              <div className="w-6 h-6 border-2 border-[#2B3380] border-t-transparent rounded-full animate-spin" />
              <span className="text-xs">Loading audit snapshots...</span>
            </div>
          )}

          {error && (
            <div className="p-4 bg-rose-50 border border-rose-200 rounded-xl flex items-start gap-2.5 text-rose-700 text-xs">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {!loading && !error && versions.length === 0 && (
            <div className="py-12 text-center text-slate-400">
              <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm font-semibold">No version snapshots yet</p>
              <p className="text-xs text-slate-400 mt-1">
                Version snapshots are created automatically every time this page is published.
              </p>
            </div>
          )}

          {!loading && !error && versions.map((v) => (
            <div
              key={v.id}
              className="p-4 rounded-xl border border-slate-200 hover:border-slate-300 bg-white transition hover:shadow-xs flex items-center justify-between gap-4"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded-md bg-[#2B3380]/10 text-[#2B3380] text-[11px] font-bold">
                    v{v.version_number || 1}
                  </span>
                  <span className="text-xs font-semibold text-slate-800 truncate">
                    {v.page_title || v.note || 'Snapshot'}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-500">
                  <span>{formatDate(v.created_at)}</span>
                  {v.created_by && <span>by {v.created_by}</span>}
                  {v.blocks_data?.length > 0 && (
                    <span>• {v.blocks_data.length} block(s)</span>
                  )}
                </div>
                {v.note && (
                  <p className="text-[11px] text-slate-600 mt-1 italic">"{v.note}"</p>
                )}
              </div>

              <button
                type="button"
                disabled={rollingBackId === v.id}
                onClick={() => setConfirmVersion(v)}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 hover:border-[#2B3380] hover:text-[#2B3380] hover:bg-[#2B3380]/5 transition flex items-center gap-1.5 flex-shrink-0"
              >
                {rollingBackId === v.id ? (
                  <>
                    <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    <span>Restoring...</span>
                  </>
                ) : (
                  <>
                    <RotateCcw className="w-3.5 h-3.5" />
                    <span>Restore</span>
                  </>
                )}
              </button>
            </div>
          ))}
        </div>

        {/* Confirmation Modal Overlay if clicked */}
        {confirmVersion && (
          <div className="absolute inset-0 bg-white/95 backdrop-blur-xs p-6 flex flex-col justify-center items-center text-center z-20">
            <div className="w-12 h-12 rounded-2xl bg-amber-100 text-amber-600 flex items-center justify-center mb-3">
              <RotateCcw className="w-6 h-6" />
            </div>
            <h4 className="text-base font-bold text-slate-900">
              Restore Version {confirmVersion.version_number}?
            </h4>
            <p className="text-xs text-slate-600 max-w-sm mt-1.5">
              This will restore the page content and blocks to the snapshot from{' '}
              <span className="font-semibold">{formatDate(confirmVersion.created_at)}</span>.
              A safety snapshot of your current state will be created first.
            </p>
            <div className="flex items-center gap-3 mt-6">
              <button
                type="button"
                onClick={() => setConfirmVersion(null)}
                className="px-4 py-2 text-xs font-semibold rounded-xl border border-slate-300 text-slate-700 hover:bg-slate-100 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleExecuteRollback(confirmVersion)}
                className="px-4 py-2 text-xs font-bold rounded-xl bg-[#2B3380] text-white hover:bg-[#222968] shadow-md transition flex items-center gap-1.5"
              >
                <CheckCircle className="w-4 h-4" />
                <span>Confirm Restore</span>
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between text-xs text-slate-500">
          <span>Safe Rollback: An auto-snapshot is taken before any restore.</span>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-200/70 font-semibold transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
